/**
 * Learning Center API: image packs (WP-75, lms-plan 18.34; the routes on
 * routes/lms/catalog.js over services/lms/courses/imagePack.js). A pack is a
 * set of reviewed illustrations for one course whose PLACEMENT ships with the
 * code as a manifest (section, after which slide, heading, alt, caption); the
 * admin uploads the image files, or the one zip Canva downloads them as, and
 * the server places them on the course's draft (made from the current version
 * when none is open). Nothing is published: the admin reads the draft and
 * clicks Publish (G8). Typed calls over services/api; nothing under src/lms
 * imports axios (2.3a).
 *
 *   GET  /admin/courses/:id/image-packs                    -> { items: ImagePackSummary[] }
 *   POST /admin/courses/:id/image-packs/:packId/apply (multipart files[]) -> ImagePackResult
 *
 * The FormData is posted with no explicit Content-Type: the browser sets the
 * multipart boundary itself (the external-certificate upload's pattern).
 */
import api from '../../services/api';
import type { VersionDetail } from './catalog';

/** services/lms/courses/imagePack.js IMAGE_PACK_MAX_FILES: files in one upload (loose, or inside the zip). */
export const IMAGE_PACK_MAX_FILES = 60;
/** services/lms/courses/imagePack.js IMAGE_PACK_UPLOAD_MAX_BYTES: one uploaded file (the zip) at most. */
export const IMAGE_PACK_UPLOAD_MAX_BYTES = 120 * 1024 * 1024;
/** What the file input accepts: the three image types (D10, never SVG) and a zip of them. */
export const IMAGE_PACK_ACCEPT = '.png,.jpg,.jpeg,.webp,.zip,image/png,image/jpeg,image/webp,application/zip';

export interface ImagePackItem {
  ordinal: number;
  file: string;
  section_id: string;
  after_slide_id: string | null;
  heading: string;
}

export interface ImagePackSummary {
  id: string;
  course_slug: string;
  title: string;
  description: string | null;
  item_count: number;
  items: ImagePackItem[];
}

export interface ImagePackPlaced {
  ordinal: number;
  file: string;
  uploaded_as: string;
  matched_by: 'name' | 'order';
  slide_id: string;
  section_id: string;
  asset_id: string;
}

export interface ImagePackSkipped {
  ordinal: number;
  file: string;
  reason: 'already_placed';
  slide_id: string;
  asset_id: string;
}

export interface ImagePackWarning {
  kind: string;
  ordinal?: number;
  section_id?: string;
  after_slide_id?: string | null;
}

export interface ImagePackResult {
  pack: { id: string; title: string; course_slug: string; item_count: number };
  version: VersionDetail;
  created_draft: boolean;
  placed: ImagePackPlaced[];
  skipped: ImagePackSkipped[];
  warnings: ImagePackWarning[];
}

const enc = encodeURIComponent;

/** GET /api/lms/admin/courses/:id/image-packs: the packs shipped for this course's slug (often none). */
export async function listImagePacks(courseId: string): Promise<ImagePackSummary[]> {
  const response = await api.get<{ items: ImagePackSummary[] }>(`/api/lms/admin/courses/${enc(courseId)}/image-packs`);
  return Array.isArray(response.data?.items) ? response.data.items : [];
}

/** The multipart body: every file under the one field `files`, in the order chosen. */
export function buildImagePackForm(files: File[]): FormData {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  return form;
}

/** POST /api/lms/admin/courses/:id/image-packs/:packId/apply (multipart). */
export async function applyImagePack(courseId: string, packId: string, files: File[]): Promise<ImagePackResult> {
  const response = await api.post<ImagePackResult>(
    `/api/lms/admin/courses/${enc(courseId)}/image-packs/${enc(packId)}/apply`,
    buildImagePackForm(files),
  );
  return response.data;
}

const IMAGE_EXT_RE = /\.(png|jpe?g|webp)$/i;
const ZIP_EXT_RE = /\.zip$/i;

/**
 * The device-side check before anything is posted: images or zips only (by
 * name or type), at most IMAGE_PACK_MAX_FILES, none over the upload cap. The
 * server sniffs the bytes and matches the pack; this only stops the plainly
 * wrong. Null when the set may be sent.
 */
export function packFilesProblem(files: File[], pack?: Pick<ImagePackSummary, 'item_count'> | null): string | null {
  if (files.length === 0) return 'Choose the pack images, or the zip Canva downloaded them as.';
  if (files.length > IMAGE_PACK_MAX_FILES) return `At most ${IMAGE_PACK_MAX_FILES} files in one upload.`;
  for (const file of files) {
    const type = String(file.type || '');
    const isImage = IMAGE_EXT_RE.test(file.name) || /^image\/(png|jpeg|webp)$/.test(type);
    const isZip = ZIP_EXT_RE.test(file.name) || /zip/.test(type);
    if (!isImage && !isZip) return `${file.name} is not a PNG, JPEG or WebP image, or a zip of them.`;
    if (file.size === 0) return `${file.name} is empty.`;
    if (file.size > IMAGE_PACK_UPLOAD_MAX_BYTES) return `${file.name} is larger than 120 MB.`;
  }
  const loose = files.filter((f) => !ZIP_EXT_RE.test(f.name) && !/zip/.test(String(f.type || '')));
  if (pack && loose.length > 0 && loose.length === files.length && loose.length < pack.item_count) {
    return `This pack has ${pack.item_count} images; ${loose.length} chosen. Choose them all, or the zip.`;
  }
  return null;
}

/** "Section s3, after slide s3-1" for the modal's table. */
export function placementLabel(item: Pick<ImagePackItem, 'section_id' | 'after_slide_id'>): string {
  return item.after_slide_id ? `Section ${item.section_id}, after slide ${item.after_slide_id}` : `Section ${item.section_id}, at the end`;
}
